import MDAlert from "components/MDAlert";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import { UserRole, UserStatus } from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import useGetOrganizationUsers from "hooks/users/useGetOrganizationUsers";
import { uniq } from "ramda";
import React, { useMemo } from "react";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";
import { getFullName } from "utils/people/getFullName";

const userMap = (user) => ({
  label: getFullName(user),
  value: user.id,
  hasEmployee: !!user.hasEmployee,
});

type ArchivedUser = { id: string; firstName?: string; lastName?: string } | null | undefined;

export default function OrganizationUsersOptions({
  children,
  selected,
  archivedUser,
  queryOverride,
}: {
  children: ({
    organizationUsersOptions,
    loading,
    search,
    isOptionDisabled,
  }: {
    organizationUsersOptions: SelectOptions;
    loading: boolean;
    search: (v: string) => void;
    isOptionDisabled: (option: SelectOptions[number]) => boolean;
  }) => React.ReactNode;
  selected?: SelectOptions[number];
  archivedUser?: ArchivedUser;
  queryOverride?: { first?: number; roles?: UserRole[]; status?: UserStatus };
}) {
  const { filtering } = usePaginatedVariables();

  const { loading, data, error } = useGetOrganizationUsers({
    page: 1,
    first: 10,
    search: filtering.debouncedSearchTerm,
    ...(queryOverride && queryOverride),
  });

  const users = data ?? [];

  const { organizationUsersOptions, archivedOptionValue } = useMemo(() => {
    let options: SelectOptions = selected
      ? uniq([...users.map(userMap), { ...selected, hasEmployee: selected.hasEmployee ?? false }])
      : users.map(userMap);

    let archivedOptionValue: string | null = null;

    if (archivedUser?.id && !options.some((o) => o.value === archivedUser.id)) {
      const name = `${archivedUser.firstName ?? ""} ${archivedUser.lastName ?? ""}`.trim();
      archivedOptionValue = archivedUser.id;
      options = [
        { value: archivedUser.id, label: `${name || "Unknown"} (Archived)`, hasEmployee: false },
        ...options,
      ];
    }

    return { organizationUsersOptions: options, archivedOptionValue };
  }, [users, selected, archivedUser]);

  const isOptionDisabled = useMemo(() => {
    return (option: SelectOptions[number]) =>
      archivedOptionValue != null && option.value === archivedOptionValue;
  }, [archivedOptionValue]);

  if (error) {
    return <MDAlert color="error">{parseGraphQLError(error)}</MDAlert>;
  }

  return (
    <>
      {children({
        organizationUsersOptions,
        loading,
        search: filtering.setSearchTerm,
        isOptionDisabled,
      })}
    </>
  );
}
